/* eslint complexity: 0 */
import { create_handoff_drawer } from './onboarding_handoff_base_helpers';

const journey_route_map = {
  Supplier: 'SupplierRegister',
  Employee: 'EmployeeRegister',
  Client: 'ContractLifecycle',
  Partner: 'ContractLifecycle',
  Merchant: 'FinanceCompliance'
};

const journey_register_map = {
  Supplier: 'supplier compliance register',
  Employee: 'employee compliance register',
  Client: 'contract register',
  Partner: 'partner compliance register',
  Merchant: 'merchant compliance register'
};

function handoff_route_name(journey) {
  return Reflect.get(journey_route_map, journey) || 'OnboardingHandoff';
}

function handoff_register_name(journey) {
  return Reflect.get(journey_register_map, journey) || 'compliance register';
}

function document_status_note(status) {
  const status_note_map = {
    verified: 'Verified on file',
    received: 'Received at intake',
    expired: 'Expired and needs renewal',
    missing: 'Not yet on file'
  };
  return Reflect.get(status_note_map, status) || 'Awaiting action';
}

function document_meta(row) {
  return {
    has_expiry: row.expires !== '-',
    has_reference: row.reference !== '-'
  };
}

function route_value(meta) {
  const value = meta.has_expiry ? 'Tracked' : 'No expiry';
  return value;
}

function route_note(meta, row) {
  return meta.has_expiry ? row.expires : 'No review date';
}

function build_document_stats(row, register_name, expiry_note, status_note, is_problem) {
  const meta = document_meta(row);
  const primary_accent = is_problem ? 'orange' : 'green';
  const status_accent = is_problem ? 'red' : 'green';
  return [
    { label: 'Entity', value: row.entity, note: row.journey, accent: 'green' },
    { label: 'Reference', value: row.reference, note: expiry_note, accent: primary_accent },
    { label: 'Document state', value: row.status, note: status_note, accent: status_accent },
    { label: 'Register owner', value: register_name, note: 'Inbound document', accent: 'green' },
    { label: 'Route', value: route_value(meta), note: route_note(meta, row), accent: primary_accent }
  ];
}

function document_problem_title(status) {
  const title_map = {
    Expired: 'Renew document',
    Missing: 'Obtain and upload document'
  };
  return Reflect.get(title_map, status) || 'Resolve document exception';
}

function build_document_obligations(row, register_name, is_problem) {
  const meta = document_meta(row);
  if (!is_problem) {
    return [
      { title: 'Keep document on record', subtitle: `Owned by ${row.entity}`, status: 'Scheduled', status_class: 'bg-success-subtle text-success-emphasis' },
      { title: 'Review on expiry cycle', subtitle: meta.has_expiry ? row.expires : 'No expiry recorded', status: 'Scheduled', status_class: 'bg-success-subtle text-success-emphasis' }
    ];
  }

  return [
    { title: document_problem_title(row.status), subtitle: meta.has_reference ? row.reference : 'No reference recorded', status: 'Monitor', status_class: 'bg-warning-subtle text-warning-emphasis' },
    { title: 'Notify register owner', subtitle: register_name, status: 'Monitor', status_class: 'bg-warning-subtle text-warning-emphasis' }
  ];
}

function build_document_downstream(row, is_problem, register_name) {
  const meta = document_meta(row);
  const status_label = is_problem ? 'Pending' : 'Created';
  const downstream_status_class = is_problem ? 'bg-warning-subtle text-warning-emphasis' : 'bg-success-subtle text-success-emphasis';
  return [
    { title: `${register_name} updated`, meta: `${row.entity} - ${row.document}`, status: status_label, status_class: downstream_status_class },
    { title: 'Evidence vault entry', meta: meta.has_reference ? row.reference : 'Awaiting file upload', status: status_label, status_class: downstream_status_class },
    { title: 'Renewal / review reminder', meta: meta.has_expiry ? `Due ${row.expires}` : 'No expiry', status: status_label, status_class: downstream_status_class }
  ];
}

function document_drawer_intro(is_problem) {
  return is_problem
    ? 'This document needs attention before the record can move cleanly through the compliance lifecycle.'
    : 'This document is filed and is now part of the permanent compliance record for this entity.';
}

function build_document_details(row, is_problem, expiry_note) {
  const meta = document_meta(row);
  return [
    { title: row.document, meta: `${row.entity} - ${row.journey}`, status: row.status, status_class: row.status_class, icon: is_problem ? 'bi-file-earmark-x' : 'bi-file-earmark-text' },
    { title: 'Reference', meta: row.reference, status: meta.has_expiry ? 'Tracked' : 'Open', status_class: 'bg-secondary-subtle text-secondary-emphasis', icon: 'bi-link-45deg' },
    { title: 'Expiry', meta: expiry_note, status: meta.has_expiry ? 'Scheduled' : 'None', status_class: 'bg-secondary-subtle text-secondary-emphasis', icon: 'bi-calendar2-event' }
  ];
}

function build_document_approvals(row, is_problem) {
  return [
    { title: 'Captured in onboarding handoff', subtitle: `${row.entity} - ${row.document}`, icon: 'bi-check2-circle' },
    { title: is_problem ? 'Follow-up required' : 'Ready for monitoring', subtitle: is_problem ? 'Compliance queue updated' : 'Monitoring feeds active', icon: is_problem ? 'bi-exclamation-circle' : 'bi-check2-circle' }
  ];
}

function document_drawer_payload(row, route_name, register_name, expiry_note, status_note, is_problem) {
  const payload = {
    section: `DOCUMENT REGISTER - ${row.entity.toUpperCase()} - ${row.document.toUpperCase()}`,
    title: row.document,
    subtitle: `${row.entity} - ${row.journey} - ${row.reference}`,
    status: row.status,
    status_class: row.status_class,
    intro: document_drawer_intro(is_problem),
    stats: build_document_stats(row, register_name, expiry_note, status_note, is_problem),
    risk_label: 'Document posture',
    risk_status: row.status,
    risk_status_class: row.status_class,
    risk_tags: [
      row.reference === '-' ? 'Reference missing' : `Reference: ${row.reference}`,
      row.expires === '-' ? 'No expiry recorded' : `Expiry ${row.expires}`,
      row.journey
    ],
    documents_label: 'Document details',
    documents_status: row.status,
    documents: build_document_details(row, is_problem, expiry_note),
    approvals_label: 'Register actions',
    approvals_status: is_problem ? 'Needs action' : 'Recorded',
    approvals: build_document_approvals(row, is_problem),
    obligations_label: 'Outstanding actions',
    obligations_status: is_problem ? 'Open' : 'None',
    obligations: build_document_obligations(row, register_name, is_problem),
    downstream_label: 'What this document creates - downstream',
    downstream_status: is_problem ? 'Pending' : 'Created',
    downstream: build_document_downstream(row, is_problem, register_name),
    footer_primary_label: `Open ${row.entity} record`,
    footer_primary_route_name: route_name,
    footer_secondary_label: 'Close'
  };
  return payload;
}

export function create_document_drawer(row) {
  const status = String(row.status || '').toLowerCase();
  const is_problem = ['missing', 'expired', 'pending'].includes(status);
  const route_name = handoff_route_name(row.journey);
  const register_name = handoff_register_name(row.journey);
  const expiry_note = row.expires !== '-' ? `Expires ${row.expires}` : 'No expiry set';
  const status_note = document_status_note(status);
  return create_handoff_drawer(document_drawer_payload(row, route_name, register_name, expiry_note, status_note, is_problem));
}
